import type { DestinationEvent } from '@eventbrite/consumer-search.event-renderer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { FeaturedEventResponse } from '@/pages/api/organizers/[organizerId]/featured-event.page';
import { withBasePath } from '@/utils/withBasePath';

const FEATURED_EVENT_QUERY_KEY = 'organizer-featured-event';

function queryKey(organizerId: string) {
  return [FEATURED_EVENT_QUERY_KEY, organizerId] as const;
}

async function responseError(response: Response, message: string): Promise<Error> {
  let upstreamError = '';
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === 'string') upstreamError = `: ${body.error}`;
  } catch {
    // The service can legitimately return an empty error response.
  }

  return new Error(`${message} (${response.status || 'unknown'}${upstreamError})`);
}

async function fetchFeaturedEvent(organizerId: string): Promise<DestinationEvent | null> {
  const response = await fetch(withBasePath(`/api/organizers/${organizerId}/featured-event`), {
    method: 'GET',
    cache: 'no-store',
    credentials: 'include',
  });
  if (!response.ok) throw await responseError(response, 'Unable to load the highlighted event');
  return ((await response.json()) as FeaturedEventResponse).event;
}

async function updateFeaturedEvent(
  organizerId: string,
  event: DestinationEvent | null,
): Promise<void> {
  const response = await fetch(withBasePath(`/api/organizers/${organizerId}/featured-event`), {
    method: 'PUT',
    cache: 'no-store',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ eventId: event?.id ?? null }),
  });
  if (!response.ok) throw await responseError(response, 'Unable to update the highlighted event');
}

interface UseFeaturedEventArgs {
  organizerId: string;
  initialEvent: DestinationEvent | null;
  enabled: boolean;
}

export function useFeaturedEvent({ organizerId, initialEvent, enabled }: UseFeaturedEventArgs) {
  const client = useQueryClient();
  const key = queryKey(organizerId);
  const query = useQuery<DestinationEvent | null>({
    queryKey: key,
    queryFn: () => fetchFeaturedEvent(organizerId),
    initialData: initialEvent,
    enabled: enabled && Boolean(organizerId),
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation({
    mutationFn: (event: DestinationEvent | null) => updateFeaturedEvent(organizerId, event),
    onMutate: async (event) => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<DestinationEvent | null>(key) ?? null;
      client.setQueryData(key, event);
      return { previous };
    },
    onError: (_error, _event, context) => {
      client.setQueryData(key, context?.previous ?? null);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: key });
    },
  });

  return {
    event: query.data ?? null,
    isRefreshing: query.isFetching,
    update: mutation.mutateAsync,
    isUpdating: mutation.isLoading,
  };
}
